import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocation } from '@/contexts/LocationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import type { ConsumableType, ConsumableTransaction, ConsumableLocationStock } from '../types';

// These tables are not yet in the generated Supabase types (migration pending).
// We use an untyped client reference to bypass TypeScript's table checking.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ============================================================================
// QUERY KEYS
// ============================================================================

const consumableKeys = {
  all: ['consumables'] as const,
  types: (orgId: string, locationId?: string) =>
    [...consumableKeys.all, 'types', orgId, locationId ?? 'all'] as const,
  transactions: (orgId: string, locationId?: string, typeId?: string) =>
    [...consumableKeys.all, 'transactions', orgId, locationId ?? 'all', typeId] as const,
  locationStock: (orgId: string) =>
    [...consumableKeys.all, 'locationStock', orgId] as const,
};

// ============================================================================
// QUERIES
// ============================================================================

export function useConsumableTypes() {
  const { organization } = useOrganization();
  const { selectedLocation } = useLocation();
  const orgId = organization?.id ?? '';
  const locationId = selectedLocation?.id;

  return useQuery({
    queryKey: consumableKeys.types(orgId, locationId),
    queryFn: async (): Promise<ConsumableType[]> => {
      // Always fetch the base consumable_types (aggregate stock_on_hand maintained by trigger)
      const { data: types, error } = await db
        .from('consumable_types')
        .select('*')
        .eq('organization_id', orgId)
        .order('name');

      if (error) throw new Error(error.message);
      const baseTypes = (types ?? []) as ConsumableType[];

      if (!locationId) {
        // All Locations: return aggregate stock_on_hand (kept in sync by trigger)
        return baseTypes;
      }

      // Specific location: fetch location stock and override stock_on_hand
      const { data: locationStock, error: lsError } = await db
        .from('consumable_location_stock')
        .select('consumable_type_id, stock_on_hand')
        .eq('organization_id', orgId)
        .eq('location_id', locationId);

      if (lsError) throw new Error(lsError.message);

      const stockMap = new Map<string, number>();
      for (const ls of (locationStock ?? []) as ConsumableLocationStock[]) {
        stockMap.set(ls.consumable_type_id, Number(ls.stock_on_hand));
      }

      return baseTypes.map((t) => ({
        ...t,
        stock_on_hand: stockMap.get(t.id) ?? 0,
      }));
    },
    enabled: !!orgId,
    staleTime: 30_000,
  });
}

export function useConsumableTransactions(consumableTypeId?: string) {
  const { organization } = useOrganization();
  const { selectedLocation } = useLocation();
  const orgId = organization?.id ?? '';
  const locationId = selectedLocation?.id;

  return useQuery({
    queryKey: consumableKeys.transactions(orgId, locationId, consumableTypeId),
    queryFn: async (): Promise<ConsumableTransaction[]> => {
      let query = db
        .from('consumable_transactions')
        .select('*')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (consumableTypeId) {
        query = query.eq('consumable_type_id', consumableTypeId);
      }

      if (locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;

      if (error) throw new Error(error.message);
      return (data ?? []) as ConsumableTransaction[];
    },
    enabled: !!orgId,
    staleTime: 15_000,
  });
}

// ============================================================================
// MUTATIONS
// ============================================================================

export function useConsumableMutations() {
  const { organization } = useOrganization();
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const orgId = organization?.id;
  const locationId = selectedLocation?.id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const requireOrg = () => {
    if (!orgId) throw new Error('Organization not loaded');
    return orgId;
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: consumableKeys.all });
  };

  // --- Consumable Types ---

  const createType = useMutation({
    mutationFn: async (input: {
      name: string;
      unit: string;
      category?: string;
      unit_cost?: number;
      low_stock_threshold?: number;
      stock_on_hand?: number;
    }) => {
      const org = requireOrg();
      const initialStock = input.stock_on_hand ?? 0;

      // Create the type with 0 stock (trigger will compute aggregate)
      const { data, error } = await db
        .from('consumable_types')
        .insert({
          organization_id: org,
          name: input.name,
          unit: input.unit,
          category: input.category || null,
          unit_cost: input.unit_cost ?? null,
          low_stock_threshold: input.low_stock_threshold ?? null,
          stock_on_hand: 0,
        })
        .select()
        .single();

      if (error) throw new Error(error.message);
      const created = data as ConsumableType;

      // If initial stock > 0 and a location is selected, create the location stock row
      if (initialStock > 0 && locationId) {
        const { error: clsError } = await db
          .from('consumable_location_stock')
          .insert({
            organization_id: org,
            consumable_type_id: created.id,
            location_id: locationId,
            stock_on_hand: initialStock,
          });
        if (clsError) throw new Error(clsError.message);

        // Record initial stock transaction
        const { error: txnErr } = await db
          .from('consumable_transactions')
          .insert({
            organization_id: org,
            consumable_type_id: created.id,
            location_id: locationId,
            transaction_type: 'add',
            quantity: initialStock,
            stock_before: 0,
            stock_after: initialStock,
            unit_cost: input.unit_cost ?? null,
            notes: 'Initial stock on creation',
            user_id: user?.id ?? null,
          });
        if (txnErr) throw new Error(txnErr.message);
      }

      return created;
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Success', description: 'Consumable type created.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create consumable type.',
        variant: 'destructive',
      });
    },
  });

  const updateType = useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      unit: string;
      category?: string;
      unit_cost?: number;
      low_stock_threshold?: number;
      is_active?: boolean;
    }) => {
      const { id, ...updates } = input;
      const { data, error } = await db
        .from('consumable_types')
        .update({
          name: updates.name,
          unit: updates.unit,
          category: updates.category || null,
          unit_cost: updates.unit_cost ?? null,
          low_stock_threshold: updates.low_stock_threshold ?? null,
          is_active: updates.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data as ConsumableType;
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Success', description: 'Consumable type updated.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update consumable type.',
        variant: 'destructive',
      });
    },
  });

  const deleteType = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db
        .from('consumable_types')
        .delete()
        .eq('id', id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Success', description: 'Consumable type deleted.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete consumable type.',
        variant: 'destructive',
      });
    },
  });

  // --- Stock Operations ---

  const addStock = useMutation({
    mutationFn: async (input: {
      consumableTypeId: string;
      quantity: number;
      notes?: string;
      locationId?: string;
    }) => {
      const org = requireOrg();
      const targetLocationId = input.locationId ?? locationId;
      if (!targetLocationId) {
        throw new Error('Please select a specific location to adjust stock.');
      }

      // 1. Read current location stock (upsert row if missing)
      const { data: existing, error: readErr } = await db
        .from('consumable_location_stock')
        .select('id, stock_on_hand')
        .eq('organization_id', org)
        .eq('consumable_type_id', input.consumableTypeId)
        .eq('location_id', targetLocationId)
        .maybeSingle();

      if (readErr) throw new Error(readErr.message);

      const stockBefore = existing ? Number(existing.stock_on_hand) : 0;
      const stockAfter = stockBefore + input.quantity;

      // 2. Fetch unit_cost from type
      const { data: typeData, error: typeErr } = await db
        .from('consumable_types')
        .select('unit_cost')
        .eq('id', input.consumableTypeId)
        .single();
      if (typeErr) throw new Error(typeErr.message);

      // 3. Upsert consumable_location_stock
      if (existing) {
        const { error: updateErr } = await db
          .from('consumable_location_stock')
          .update({
            stock_on_hand: stockAfter,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
        if (updateErr) throw new Error(updateErr.message);
      } else {
        const { error: insertErr } = await db
          .from('consumable_location_stock')
          .insert({
            organization_id: org,
            consumable_type_id: input.consumableTypeId,
            location_id: targetLocationId,
            stock_on_hand: stockAfter,
          });
        if (insertErr) throw new Error(insertErr.message);
      }

      // 4. Insert transaction
      const { error: txnErr } = await db
        .from('consumable_transactions')
        .insert({
          organization_id: org,
          consumable_type_id: input.consumableTypeId,
          location_id: targetLocationId,
          transaction_type: 'add',
          quantity: input.quantity,
          stock_before: stockBefore,
          stock_after: stockAfter,
          unit_cost: typeData.unit_cost ?? null,
          notes: input.notes || null,
          user_id: user?.id ?? null,
        });

      if (txnErr) throw new Error(txnErr.message);
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Stock added', description: 'Inventory updated successfully.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add stock.',
        variant: 'destructive',
      });
    },
  });

  const consumeStock = useMutation({
    mutationFn: async (input: {
      consumableTypeId: string;
      quantity: number;
      notes?: string;
      locationId?: string;
    }) => {
      const org = requireOrg();
      const targetLocationId = input.locationId ?? locationId;
      if (!targetLocationId) {
        throw new Error('Please select a specific location to adjust stock.');
      }

      // 1. Read current location stock
      const { data: existing, error: readErr } = await db
        .from('consumable_location_stock')
        .select('id, stock_on_hand')
        .eq('organization_id', org)
        .eq('consumable_type_id', input.consumableTypeId)
        .eq('location_id', targetLocationId)
        .maybeSingle();

      if (readErr) throw new Error(readErr.message);

      const stockBefore = existing ? Number(existing.stock_on_hand) : 0;
      const stockAfter = stockBefore - input.quantity;

      // 2. Fetch unit_cost from type
      const { data: typeData, error: typeErr } = await db
        .from('consumable_types')
        .select('unit_cost')
        .eq('id', input.consumableTypeId)
        .single();
      if (typeErr) throw new Error(typeErr.message);

      // 3. Upsert consumable_location_stock
      if (existing) {
        const { error: updateErr } = await db
          .from('consumable_location_stock')
          .update({
            stock_on_hand: stockAfter,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
        if (updateErr) throw new Error(updateErr.message);
      } else {
        const { error: insertErr } = await db
          .from('consumable_location_stock')
          .insert({
            organization_id: org,
            consumable_type_id: input.consumableTypeId,
            location_id: targetLocationId,
            stock_on_hand: stockAfter,
          });
        if (insertErr) throw new Error(insertErr.message);
      }

      // 4. Insert transaction
      const { error: txnErr } = await db
        .from('consumable_transactions')
        .insert({
          organization_id: org,
          consumable_type_id: input.consumableTypeId,
          location_id: targetLocationId,
          transaction_type: 'consume',
          quantity: input.quantity,
          stock_before: stockBefore,
          stock_after: stockAfter,
          unit_cost: typeData.unit_cost ?? null,
          notes: input.notes || null,
          user_id: user?.id ?? null,
        });

      if (txnErr) throw new Error(txnErr.message);
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Stock consumed', description: 'Inventory updated successfully.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to consume stock.',
        variant: 'destructive',
      });
    },
  });

  const adjustStock = useMutation({
    mutationFn: async (input: {
      consumableTypeId: string;
      newQuantity: number;
      notes?: string;
      locationId?: string;
    }) => {
      const org = requireOrg();
      const targetLocationId = input.locationId ?? locationId;
      if (!targetLocationId) {
        throw new Error('Please select a specific location to adjust stock.');
      }

      // 1. Read current location stock
      const { data: existing, error: readErr } = await db
        .from('consumable_location_stock')
        .select('id, stock_on_hand')
        .eq('organization_id', org)
        .eq('consumable_type_id', input.consumableTypeId)
        .eq('location_id', targetLocationId)
        .maybeSingle();

      if (readErr) throw new Error(readErr.message);

      const stockBefore = existing ? Number(existing.stock_on_hand) : 0;
      const stockAfter = input.newQuantity;
      const quantity = stockAfter - stockBefore;

      // 2. Fetch unit_cost from type
      const { data: typeData, error: typeErr } = await db
        .from('consumable_types')
        .select('unit_cost')
        .eq('id', input.consumableTypeId)
        .single();
      if (typeErr) throw new Error(typeErr.message);

      // 3. Upsert consumable_location_stock
      if (existing) {
        const { error: updateErr } = await db
          .from('consumable_location_stock')
          .update({
            stock_on_hand: stockAfter,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
        if (updateErr) throw new Error(updateErr.message);
      } else {
        const { error: insertErr } = await db
          .from('consumable_location_stock')
          .insert({
            organization_id: org,
            consumable_type_id: input.consumableTypeId,
            location_id: targetLocationId,
            stock_on_hand: stockAfter,
          });
        if (insertErr) throw new Error(insertErr.message);
      }

      // 4. Insert transaction
      const { error: txnErr } = await db
        .from('consumable_transactions')
        .insert({
          organization_id: org,
          consumable_type_id: input.consumableTypeId,
          location_id: targetLocationId,
          transaction_type: 'adjust',
          quantity,
          stock_before: stockBefore,
          stock_after: stockAfter,
          unit_cost: typeData.unit_cost ?? null,
          notes: input.notes || null,
          user_id: user?.id ?? null,
        });

      if (txnErr) throw new Error(txnErr.message);
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Stock adjusted', description: 'Inventory updated successfully.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to adjust stock.',
        variant: 'destructive',
      });
    },
  });

  return {
    createType,
    updateType,
    deleteType,
    addStock,
    consumeStock,
    adjustStock,
    /** Whether a specific location is selected (stock ops are allowed) */
    hasLocationSelected: !!locationId,
    selectedLocationId: locationId,
  };
}
